import {
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
} from "react-query";
import { GATEWAY_URL } from "./constants";
import useFetchWithAuth from "../fetchWithAuth";
import useGroupQuery from "./useGroupQuery";
import { useCurrentTrip } from "@/hooks";
import { TripStatus } from "@/fetch/gworld/useTripsQuery";

const PAGE_SIZE = 10;

export type SampleLike = Partial<{
  cid: number;
  email?: string;
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  post_id?: number;
  id?: number;
  s3_profile_pic?: string;
  s3_resume_pic?: string;
  country?: string;
  division?: string;
  nickname?: string;
  status?: TripStatus;
  is_suspended: number;
}>;

export type SampleComment = Partial<{
  id: number;
  country: string;
  division: string;
  email: string;
  post_id: number;
  cid: number;
  content: string;
  nickname?: string;
  s3_profile_pic: string;
  s3_resume_pic: string;
  is_suspended: number;
  status: TripStatus;
}>;

export type Media = Partial<{
  id: number;
  post_id: number;
  type: "Image" | "Video";
  public_id: string;
  first_name: string;
  last_name: string;
  middle_name: string;
  s3_profile_pic?: string;
  cid: number;
}>;

export type Post = Partial<{
  nickname: string;
  group_id: number;
  division: string;
  cid: number;
  content?: string;
  id: number;
  is_admin: boolean | number;
  admin_title?: string | null;
  is_suspended: number;
  likes: { total_likes: number; sample_likes: SampleLike[] };
  comments: { total_comments: number; sample_comments: SampleComment[] };
  media: Media[];
  status?: "FINAL" | "DRAFT";
  trip_status?: TripStatus;
  s3_profile_pic: string;
  s3_resume_pic: string;
  country: string;
  _ca: number;
}>;

type RecentPostsParam = {
  pageSize?: number;
  withMedia?: boolean;
  pinned?: boolean;
  adminPosts?: boolean;
  lastId?: number;
  lastCa?: number;
};

export type RecentPostsFormattedResult = Array<Post>;

export type RecentPostsQueryResult = {
  posts: Array<Post>;
  media: { [postId: string | number]: Array<Media> };
  likes: {
    [postId: string | number]: {
      totalLikes: number;
      sampleLikes: SampleLike[];
    };
  };
  comments: {
    [postId: string | number]: {
      totalComments: number;
      sampleComments: SampleComment[];
    };
  };
  travellers: {
    [cid: number | string]: {
      cid: number;
      nickname: string;
      email: string;
      country: string;
      division: string;
      trip_status: string;
      s3_profile_pic: string;
    };
  };
};

export type RecentPostsFetchResult = {
  data: RecentPostsQueryResult;
  statusCode: number;
  success: boolean | string;
};

export type InfinityResult = {
  pages: Array<Array<Post>>;
  pageParam: any[];
};
type QueryError = {};

export const formatPostsData = (newPostData: RecentPostsQueryResult) => {
  const { posts, likes, comments, media, travellers } = newPostData;
  const formattedPosts: Array<Post | null> = posts.map((eachPost) => {
    const postLikes = likes[eachPost.id as number];
    const postComments = comments[eachPost.id as number];
    const postMedia = media[eachPost.id as number];
    const postTraveller = travellers[eachPost.cid as number];

    const sampleComments = postComments.sampleComments.map((eachComment) => {
      const commentTraveller = travellers[eachComment.cid as number];
      return {
        ...eachComment,
        nickname: commentTraveller?.nickname || "",
        s3_profile_pic: commentTraveller?.s3_profile_pic || "",
        division: commentTraveller?.division,
      };
    });

    if (!postTraveller) {
      return null;
    }

    return {
      ...eachPost,
      likes: {
        sample_likes: postLikes.sampleLikes,
        total_likes: postLikes.totalLikes,
      },
      comments: {
        sample_comments: sampleComments,
        total_comments: postComments.totalComments,
      },
      media: postMedia || [],
      nickname: postTraveller ? postTraveller.nickname : "Removed Traveller",
      division: postTraveller ? postTraveller.division : "Unavailable",
      s3_profile_pic: postTraveller?.s3_profile_pic || "",
      trip_status: postTraveller
        ? (postTraveller.trip_status as TripStatus)
        : TripStatus.Cancelled,
    };
  });

  const filteredFormattedPosts: Array<Post> = formattedPosts.filter(
    (each) => !!each
  ) as Array<Post>;

  return filteredFormattedPosts;
};

const useRecentPostsQuery = (
  params?: RecentPostsParam,
  options?: UseInfiniteQueryOptions<
    RecentPostsFormattedResult,
    any,
    RecentPostsFormattedResult,
    RecentPostsFormattedResult,
    QueryKey
  >
) => {
  const { data: group, isLoading: isGroupLoading } = useGroupQuery();
  const { fetchWithAuth } = useFetchWithAuth();
  const { currentTrip } = useCurrentTrip();
  const groupId = group?.id;
  const cid = currentTrip?.cid;

  const url = `${GATEWAY_URL}/v2/post/${groupId}`;
  const cacheKey = [url, JSON.stringify(params)];

  const queryResult = useInfiniteQuery<RecentPostsFormattedResult, QueryError>(
    cacheKey,
    async ({ pageParam }) => {
      if (!groupId || !cid) throw new Error("Group id or cid not found");

      const result = await fetchWithAuth<RecentPostsFetchResult>(url, {
        params: {
          ...params,
          pageSize: params?.pageSize ?? PAGE_SIZE,
          ...(pageParam ?? {}),
        },
      });
      const formattedPosts = formatPostsData(result.data);
      return formattedPosts;
    },
    {
      ...options,
      enabled: options?.enabled ?? Boolean(groupId && cid),
      getNextPageParam: (lastPage, pages) => {
        if (!lastPage.length) {
          return undefined;
        }

        const lastPost = lastPage[lastPage.length - 1];

        return {
          lastId: lastPost.id,
          lastCa: lastPost._ca,
        };
      },
    }
  );
  return { ...queryResult, isLoading: queryResult.isLoading || isGroupLoading };
};
export default useRecentPostsQuery;
